/**
 * Doomsworn — the character surface
 *
 * The engine has always known about Attunement, Debt, Liens, Wear and the Doom
 * Clock. The sheet did not, which meant a player could be charged for a Draw
 * they could not see and rolled a Reckoning at +0 forever because there was
 * nowhere on the page to put the casting stat.
 *
 * This module answers two questions for the generic UI: which abilities the
 * creation wizard should hand out numbers for, and what belongs on the sheet
 * besides the d20 block. Both answers are data — no component here, and
 * nothing in `apps/web` learns the word "Doomsworn" to render it.
 *
 * The ordering is deliberate. Attunement comes last among the abilities so the
 * familiar six read exactly as they always have, and the sections run
 * Doom → Debt → Runes → Wear: the thing you chose, the things you owe, the
 * things that own you, and the marks none of it will ever take back.
 */
import { ATTUNEMENT, DOOMSWORN_ABILITIES, getModifier } from './abilities.js';
import { readCharacterState } from './persistence.js';
import { DOOM_CLOCK_SEGMENTS } from './doom.js';
import { CALLING_THRESHOLDS } from './pacts.js';
import { getPower } from './powers.js';
import { GRIP_AUTONOMY_THRESHOLD, runeRatingCeiling } from './runes.js';
// ---------------------------------------------------------------------------
// CREATION
// ---------------------------------------------------------------------------
/**
 * Seven abilities, seven values.
 *
 * The 5e standard array stops at six, so Attunement gets its own value rather
 * than borrowing one — 12 is a deliberate choice, not a filler. It is enough
 * soul-weight to be worth a Power's attention (the +1 that makes a Tier-1 Draw
 * land more often than not) and low enough that a rune ceiling of 6 still costs
 * something real to reach. A character who wants more capacity must take it
 * from somewhere the same as any other ability.
 */
export const DOOMSWORN_STANDARD_ARRAY = [15, 14, 13, 12, 10, 8, 12];
export function doomswornCreationSpec() {
    return {
        abilities: DOOMSWORN_ABILITIES,
        standardArray: DOOMSWORN_STANDARD_ARRAY,
        defaultScore: 10,
        // Seven rolls for seven abilities. A pool short of the ability count would
        // leave one stat permanently at its default with no way to raise it.
        rolledPoolSize: DOOMSWORN_ABILITIES.length,
        note: 'ATTUNEMENT is soul-weight — the casting stat for Pacts, the ceiling on rune ratings ' +
            '(half the score), and your resistance in a will-contest. It is capacity, not safety: ' +
            'the more of you there is, the more there is for a Power to get hold of.',
    };
}
// ---------------------------------------------------------------------------
// SECTIONS
// ---------------------------------------------------------------------------
/**
 * Decode this system's state out of the generic envelope.
 *
 * The caller hands over `Character.systemState` verbatim as `systemData` and
 * knows nothing about what is in it; the decoding lives here, next to the
 * schema that owns the shape. `readCharacterState` is total, so a character
 * that has never touched a Power still yields a valid empty sheet.
 */
function readData(character) {
    return readCharacterState({ id: character.id, systemState: character.systemData });
}
/** Name a Power by id, falling back to the raw id for a Power this build lacks. */
function powerName(powerId) {
    return getPower(powerId)?.name ?? powerId;
}
function doomSection(doom, concessions) {
    const state = doom ?? { declared: '', die: 'd4', clock: 0, arrived: false };
    const fields = [
        {
            id: 'doom-declared',
            label: 'The end you wrote down',
            kind: 'text',
            text: state.declared,
            hint: state.declared
                ? undefined
                : 'Not declared yet. Write it in your own words — nothing will rewrite them.',
        },
        {
            id: 'doom-die',
            label: 'Doom Die',
            kind: 'text',
            text: state.die,
            tone: 'arcane',
            hint: 'Spend it to add to a roll you have already seen. Spending it is a concession, and concessions grow the die.',
        },
        {
            id: 'doom-clock',
            label: 'Doom Clock',
            kind: 'clock',
            value: state.clock,
            max: DOOM_CLOCK_SEGMENTS,
            tone: state.arrived ? 'danger' : state.clock >= DOOM_CLOCK_SEGMENTS - 2 ? 'warning' : 'neutral',
            hint: state.arrived
                ? 'It has arrived.'
                : `${DOOM_CLOCK_SEGMENTS - state.clock} segments left. The clock only ever fills.`,
        },
        {
            id: 'doom-concessions',
            label: 'Concessions',
            kind: 'meter',
            value: concessions,
            max: 10,
            hint: 'You advance by accepting costs. This is the whole track — there is no XP.',
        },
    ];
    return {
        id: 'doomsworn-doom',
        title: 'Doom',
        blurb: 'The engine owns this. The Narrator reports it and cannot write it.',
        fields,
        // Always drawn: a blank Doom is a prompt to declare one, and hiding the
        // section would hide the single most important choice on the sheet.
        showWhenEmpty: true,
    };
}
function debtSection(entries) {
    const fields = [];
    for (const entry of entries) {
        const name = powerName(entry.powerId);
        const nextThreshold = CALLING_THRESHOLDS.find(t => t > entry.debt);
        fields.push({
            id: `debt-${entry.powerId}`,
            label: `Debt — ${name}`,
            kind: 'score',
            value: entry.debt,
            tone: entry.inverted ? 'danger' : entry.debt >= 35 ? 'warning' : 'arcane',
            hint: entry.inverted
                ? 'INVERTED. You refused a Calling; this Power’s gifts now act on their own initiative.'
                : nextThreshold
                    ? `Calling at ${nextThreshold}. Debt is a tally that spends you, not a pool you spend.`
                    : 'Past every threshold. There is nothing left to warn you with.',
        });
        const liens = entry.liens.map(lien => ({
            id: lien.id,
            title: lien.claim,
            detail: name,
            resolved: lien.satisfied,
        }));
        if (liens.length) {
            fields.push({
                id: `liens-${entry.powerId}`,
                label: 'Liens',
                kind: 'entries',
                entries: liens,
                tone: 'warning',
                hint: 'Claims on you, held until satisfied.',
            });
        }
        const callings = entry.callings.map(calling => ({
            id: calling.id,
            title: calling.ask,
            detail: `${name} — ${calling.status}`,
            resolved: calling.status === 'answered',
        }));
        if (callings.length) {
            fields.push({
                id: `callings-${entry.powerId}`,
                label: 'Callings',
                kind: 'entries',
                entries: callings,
                tone: 'danger',
                hint: 'What the Power asked for. Refusal inverts your Attunement toward it.',
            });
        }
    }
    return {
        id: 'doomsworn-debt',
        title: 'Pacts',
        blurb: 'What you owe, per Power. It follows you across sessions and campaigns.',
        fields,
    };
}
function runeSection(runes, attunementScore) {
    const fields = runes.map(holding => ({
        id: `rune-${holding.rune}`,
        label: holding.rune,
        kind: 'meter',
        value: holding.grip,
        max: GRIP_AUTONOMY_THRESHOLD,
        tone: holding.grip >= GRIP_AUTONOMY_THRESHOLD
            ? 'danger'
            : holding.grip >= GRIP_AUTONOMY_THRESHOLD - 2
                ? 'warning'
                : 'arcane',
        hint: `Rating ${holding.rating} · Grip ${holding.grip}` +
            (holding.grip >= GRIP_AUTONOMY_THRESHOLD
                ? ' — it acts without you now.'
                : ` — at ${GRIP_AUTONOMY_THRESHOLD} it acts without you.`),
    }));
    const marks = runes.flatMap(holding => holding.marks.map(mark => ({
        id: mark.id,
        title: mark.description,
        detail: `${mark.rune} at Grip ${mark.atGrip}`,
    })));
    if (marks.length) {
        fields.push({
            id: 'rune-marks',
            label: 'Marks',
            kind: 'entries',
            entries: marks,
            tone: 'warning',
            hint: 'Physical and behavioural. Permanent.',
        });
    }
    // Stated even with no runes carried, because the ceiling is the reason
    // Attunement matters to a player who has not touched a rune yet.
    const ceiling = attunementScore === undefined ? undefined : runeRatingCeiling(attunementScore);
    return {
        id: 'doomsworn-runes',
        title: 'Runes',
        blurb: ceiling === undefined
            ? 'Ratings are bought with permanently sacrificed Attunement.'
            : `Ceiling ${ceiling} — half your Attunement. Ratings are bought with Attunement you never get back.`,
        fields,
    };
}
function wearSection(wear) {
    return {
        id: 'doomsworn-wear',
        title: 'Wear',
        blurb: 'There is no rest, no heal, no removal. Wear only accrues.',
        fields: [
            {
                id: 'wear-marks',
                label: 'Marks taken',
                kind: 'entries',
                tone: 'danger',
                entries: wear.map(mark => ({
                    id: mark.id,
                    title: mark.description,
                    detail: mark.source,
                })),
            },
        ],
    };
}
/**
 * Everything Doomsworn adds to the sheet below the d20 block.
 *
 * Sections with nothing in them are still returned — the sheet decides what to
 * draw via `sectionHasContent`, which keeps the "show an empty cost" judgement
 * in one place instead of duplicating it here.
 */
export function doomswornSheetSections(character) {
    const data = readData(character);
    const attunement = character.abilities?.[ATTUNEMENT];
    return [
        doomSection(data.doom, data.concessions),
        debtSection(data.entries),
        runeSection(data.runes, attunement),
        wearSection(data.wear),
    ];
}
/** Attunement modifier for display, or undefined on a sheet that has no soul-weight. */
export function attunementDisplay(character) {
    const score = character.abilities?.[ATTUNEMENT];
    if (typeof score !== 'number')
        return undefined;
    return { score, modifier: getModifier(score) };
}
//# sourceMappingURL=sheet.js.map